import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { ChefHat, CheckCircle2, Bell, RefreshCw } from 'lucide-react';

const STATIONS = ['', 'kitchen', 'bar'];
const sinceMin = (ts) => {
  if (!ts) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(ts).getTime()) / 60000));
};

export default function PosKitchen() {
  const [orders, setOrders] = useState([]);
  const [station, setStation] = useState('');
  const [loading, setLoading] = useState(false);
  const [, setTick] = useState(0); // re-render for the age timers
  const stationRef = useRef(station);
  stationRef.current = station;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = stationRef.current ? `?station=${stationRef.current}` : '';
      const res = await api.get(`/pos/kds${qs}`);
      setOrders(res.data.orders || []);
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to load tickets'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, station]);
  useEffect(() => {
    const poll = setInterval(load, 8000);
    const clock = setInterval(() => setTick((t) => t + 1), 30000);
    return () => { clearInterval(poll); clearInterval(clock); };
  }, [load]);

  const ready = async (id) => { try { await api.post(`/pos/orders/${id}/ready`); load(); } catch (e) { toast.error(e.response?.data?.error || 'Failed'); } };
  const complete = async (id) => { try { await api.post(`/pos/orders/${id}/complete`); load(); } catch (e) { toast.error(e.response?.data?.error || 'Failed'); } };

  const ageColor = (m) => m >= 15 ? 'border-red-500' : m >= 8 ? 'border-amber-500' : 'border-gray-700';

  return (
    <div className="p-4 text-gray-100 min-h-[calc(100vh-4rem)]">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold flex items-center gap-2"><ChefHat className="w-6 h-6" /> Kitchen Display</h1>
        <div className="flex items-center gap-2">
          <select value={station} onChange={(e) => setStation(e.target.value)} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm">
            {STATIONS.map((s) => <option key={s} value={s}>{s ? s : 'All stations'}</option>)}
          </select>
          <button onClick={load} className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 rounded-lg text-sm hover:bg-gray-600"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {orders.map((o) => {
          const m = sinceMin(o.paidAt || o.paid_at);
          const isReady = o.status === 'ready';
          return (
            <div key={o.id} className={`bg-gray-800 rounded-xl border-2 ${isReady ? 'border-emerald-500' : ageColor(m)} p-3 flex flex-col`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-lg">#{o.orderNumber || o.order_number}</span>
                <span className={`text-sm ${m >= 15 ? 'text-red-400' : m >= 8 ? 'text-amber-400' : 'text-gray-400'}`}>{m}m</span>
              </div>
              {(o.customerName || o.customer_name) && <div className="text-sm text-gray-400">{o.customerName || o.customer_name}</div>}
              {(o.customerTable || o.customer_table) && <div className="text-xs text-amber-300 mb-1">Table {o.customerTable || o.customer_table}{(o.customerSeat || o.customer_seat) ? ` \u00b7 Seat ${o.customerSeat || o.customer_seat}` : ''}</div>}
              {(o.staffName || o.staff_name) && <div className="text-xs text-gray-500 mb-1">By {o.staffName || o.staff_name}</div>}
              <div className="flex-1 space-y-1 mb-3">
                {(o.lines || []).filter((l) => !l.voided).map((l) => (
                  <div key={l.id} className="text-sm">
                    <span className="font-semibold text-indigo-300">{l.qty}×</span> {l.nameSnapshot || l.name_snapshot}
                    {(l.notes) && <div className="text-xs text-amber-300 ml-5">— {l.notes}</div>}
                  </div>
                ))}
              </div>
              {isReady ? (
                <button onClick={() => complete(o.id)} className="w-full py-2 bg-emerald-600 rounded-lg font-semibold hover:bg-emerald-500 flex items-center justify-center gap-2"><CheckCircle2 className="w-4 h-4" /> Bump / done</button>
              ) : (
                <button onClick={() => ready(o.id)} className="w-full py-2 bg-indigo-600 rounded-lg font-semibold hover:bg-indigo-500 flex items-center justify-center gap-2"><Bell className="w-4 h-4" /> Mark ready</button>
              )}
            </div>
          );
        })}
      </div>
      {!orders.length && !loading && <div className="text-gray-500 text-center py-16">No active tickets.</div>}
    </div>
  );
}
